"use client";

import NextLink from "next/link";
import { Box, Typography } from "@mui/material";
import {
  FONT_TEXT,
  FONT_UI,
  hairline,
} from "@/src/components/common/broadsheet/tokens";
import { renderInlineMarkup } from "@/src/utils/inline-markup";

export default function CitationEntry({
  index,
  title,
  href,
  journal,
  date,
  authors,
  extra,
}: {
  index: number;
  title: string;
  href: string;
  journal?: string;
  date?: string | null;
  authors?: string | null;
  extra?: string;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        gap: 0.875,
        alignItems: "baseline",
        py: 0.5,
        ...hairline,
        "&:last-of-type": { borderBottom: "none" },
      }}
    >
      <Box
        component="span"
        sx={{
          flex: "none",
          width: "2rem",
          fontFamily: FONT_UI,
          fontSize: "0.6875rem",
          fontWeight: 600,
          color: "custom.inkMuted",
          textAlign: "right" as const,
          flexShrink: 0,
        }}
      >
        {index + 1}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          component="h3"
          sx={{
            m: 0,
            fontFamily: FONT_TEXT,
            fontWeight: 700,
            fontSize: "0.9375rem",
            lineHeight: 1.3,
            "& a": {
              color: "custom.link",
              textDecoration: "none",
              "&:hover": {
                color: "custom.linkHover",
                textDecoration: "underline",
                textUnderlineOffset: "0.15em",
              },
            },
          }}
        >
          <NextLink href={href}>{renderInlineMarkup(title)}</NextLink>
        </Typography>
        {journal && (
          <Typography
            component="p"
            sx={{
              m: 0,
              mt: 0.0625,
              fontFamily: FONT_TEXT,
              fontStyle: "italic",
              fontSize: "0.8125rem",
              lineHeight: 1.45,
              color: "custom.inkMuted",
            }}
          >
            {renderInlineMarkup(journal)}
          </Typography>
        )}
        {date && (
          <Typography
            component="p"
            sx={{
              m: 0,
              fontFamily: FONT_UI,
              fontSize: "0.6875rem",
              lineHeight: 1.45,
              color: "custom.inkMuted",
            }}
          >
            {date}
          </Typography>
        )}
        {authors && (
          <Typography
            component="p"
            sx={{
              m: 0,
              fontFamily: FONT_UI,
              fontSize: "0.75rem",
              lineHeight: 1.45,
              color: "custom.inkMuted",
            }}
          >
            {authors}
          </Typography>
        )}
        {extra && (
          <Typography
            component="p"
            sx={{
              m: 0,
              fontFamily: FONT_UI,
              fontSize: "0.6875rem",
              lineHeight: 1.45,
              color: "custom.inkMuted",
            }}
          >
            {extra}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
